import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, AlertCircle, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';

const STATUS_ICON = {
  ok: <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />,
  warn: <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />,
  fail: <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />,
};

const STATUS_BADGE_VARIANT = {
  ok: 'default',
  warn: 'secondary',
  fail: 'destructive',
};

function CheckRow({ check }) {
  return (
    <div className="py-1.5 border-b last:border-b-0 border-border/50">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm min-w-0">
          {STATUS_ICON[check.status] || STATUS_ICON.fail}
          <span className="font-mono text-xs truncate">{check.id}</span>
        </div>
        <Badge variant={STATUS_BADGE_VARIANT[check.status] || 'destructive'} className="text-xs shrink-0">
          {check.status}
        </Badge>
      </div>
      <div className="pl-5.5 ml-0.5 text-xs text-muted-foreground mt-0.5">{check.value}</div>
      {check.detail && (
        <div className="pl-5.5 ml-0.5 text-[11px] text-muted-foreground/80 mt-0.5">{check.detail}</div>
      )}
    </div>
  );
}

export function MonitorSummaryCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    diagnosticsAPI.monitorSummary()
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to fetch monitor summary'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const checks = data ? data.checks : [];
  const failCount = checks.filter((c) => c.status === 'fail').length;
  const warnCount = checks.filter((c) => c.status === 'warn').length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-lg">Monitor Summary</CardTitle>
          {data && failCount > 0 && (
            <Badge variant="destructive" className="text-xs ml-auto">
              {failCount} failing
            </Badge>
          )}
          {data && failCount === 0 && warnCount > 0 && (
            <Badge variant="secondary" className="text-xs ml-auto">
              {warnCount} warning{warnCount !== 1 ? 's' : ''}
            </Badge>
          )}
          {data && failCount === 0 && warnCount === 0 && (
            <Badge variant="default" className="text-xs ml-auto">
              all OK
            </Badge>
          )}
        </div>
        {data && (
          <p className="text-xs text-muted-foreground">
            Generated {new Date(data.generated_at).toLocaleString()} &middot; {checks.length} checks
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
        {data && (
          <div>
            {checks.map((check) => (
              <CheckRow key={check.id} check={check} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
